import React, { useState, useEffect, useCallback } from 'react';
import { BulkPlotRequest } from '../types';
import apiService from '../services/api';
import { XMarkIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface Props {
  layoutId: number;
  onClose: () => void;
  onCreated: () => void;
}

const AddPlotModal: React.FC<Props> = ({ layoutId, onClose, onCreated }) => {
  const [layoutName, setLayoutName] = useState<string>('');
  const [plotPrefix, setPlotPrefix] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<BulkPlotRequest>({
    plot_prefix: '',
    start_number: 1,
    end_number: 1,
    size_sqft: 1000,
    size_sqm: 92.9,
    price: 0,
    facing_direction: 'North'
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadLayoutData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch layout details
      const layoutRes = await apiService.getLayoutDetails(layoutId);
      const layout = layoutRes.data.layout;
      setLayoutName(layout.name);
      
      // Generate prefix from layout name (first letter of each word)
      const prefix = layout.name
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase())
        .join('');
      setPlotPrefix(prefix);
      
      // Set initial form data with prefix
      setFormData(prev => ({ 
        ...prev, 
        plot_prefix: prefix,
        start_number: 1,
        end_number: 1
      }));
    } catch (err: any) {
      console.error('Failed to load layout data:', err);
      setError('Failed to load layout information');
    } finally {
      setLoading(false);
    }
  }, [layoutId]);

  useEffect(() => {
    loadLayoutData();
  }, [loadLayoutData]);

  const handleChange = (field: keyof BulkPlotRequest, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    if (formData.start_number > formData.end_number) {
      setError('Start number cannot be greater than end number');
      return;
    }
    if (formData.start_number < 1) {
      setError('Start number must be at least 1');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      // Ensure plot_prefix is set
      const dataToSubmit = {
        ...formData,
        plot_prefix: plotPrefix
      };
      await apiService.createBulkPlots(layoutId, dataToSubmit);
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create plots');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-700 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b flex-shrink-0">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate pr-2">Add Plots to {layoutName}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading layout information...</p>
            </div>
          ) : (
            <>
              {/* Auto-generated Prefix Info */}
              <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-2">
                  <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-blue-900">Auto-generated Plot Prefix</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Plots will be created with prefix: <span className="font-bold">{plotPrefix}</span>
                    </p>
                  </div>
                </div>
              </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Start Number *</label>
              <input
                type="number"
                className="form-input"
                value={formData.start_number}
                onChange={(e) => handleChange('start_number', parseInt(e.target.value))}
              />
            </div>
            <div>
              <label className="form-label">End Number *</label>
              <input
                type="number"
                className="form-input"
                value={formData.end_number}
                onChange={(e) => handleChange('end_number', parseInt(e.target.value))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Size (sq ft)</label>
              <input
                type="number"
                className="form-input"
                value={formData.size_sqft}
                onChange={(e) => handleChange('size_sqft', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="form-label">Size (sq m)</label>
              <input
                type="number"
                className="form-input"
                value={formData.size_sqm}
                onChange={(e) => handleChange('size_sqm', parseFloat(e.target.value))}
              />
            </div>
          </div>
          <div>
            <label className="form-label">Price (₹)</label>
            <input
              type="number"
              className="form-input"
              value={formData.price}
              onChange={(e) => handleChange('price', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <label className="form-label">Facing Direction</label>
            <select
              className="form-input"
              value={formData.facing_direction}
              onChange={(e) => handleChange('facing_direction', e.target.value)}
            >
              <option value="North">North</option>
              <option value="South">South</option>
              <option value="East">East</option>
              <option value="West">West</option>
              <option value="North-East">North-East</option>
              <option value="North-West">North-West</option>
              <option value="South-East">South-East</option>
              <option value="South-West">South-West</option>
            </select>
          </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start space-x-2">
                  <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t flex justify-end space-x-2 sm:space-x-3 flex-shrink-0">
          <button onClick={onClose} className="btn-outline px-3 sm:px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary inline-flex items-center px-3 sm:px-4 py-2 text-sm"
            disabled={submitting || loading}
          >
            {submitting ? 'Creating...' : (<><CheckIcon className="h-4 w-4 mr-1" /> Create</>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPlotModal; 